/**
 * Overview: the numbers a trainer or supervisor opens the dashboard for.
 * One window (7, 30 or 90 days), six headline figures, then where the time
 * goes: sessions per day, first-pass rate per step, the people and the
 * equipment, and the latest verdicts as they come in.
 *
 * The two chart rows are fixed-height and equal, so a short list next to a
 * tall chart cannot leave a ragged hole down the page: each panel keeps its
 * frame and scrolls its own content.
 */
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { ChartLegend, EquipmentBars, SessionsPerDay, StepFirstPass } from '../components/charts';
import {
  Dot,
  EmptyState,
  ErrorState,
  formatDuration,
  formatPercent,
  Page,
  PageHeader,
  Panel,
  Segmented,
  SkeletonRows,
  Stat,
  StatBand,
  StatusBadge,
  timeAgo,
} from '../components/patterns';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { href } from '../router';

type Window = '7' | '30' | '90';

/** Panels in a row share one height so the row reads as a row. */
const ROW_H = 'h-[400px]';

export function Overview() {
  const [window, setWindow] = useState<Window>('30');
  const days = Number(window);
  const statsQ = useQuery({
    queryKey: ['stats', 'overview', days],
    queryFn: () => api.getOverviewStats(days),
    refetchInterval: 60_000,
  });
  const stats = statsQ.data;

  return (
    <Page>
      <PageHeader
        eyebrow="Overview"
        title={greeting()}
        lede={`What happened in the last ${days} days.`}
        actions={
          <Segmented
            ariaLabel="Reporting window"
            value={window}
            onChange={setWindow}
            options={[
              { value: '7', label: '7 days' },
              { value: '30', label: '30 days' },
              { value: '90', label: '90 days' },
            ]}
          />
        }
      />

      {statsQ.error ? (
        <Panel>
          <ErrorState error={statsQ.error} retry={() => void statsQ.refetch()} />
        </Panel>
      ) : null}

      <StatBand>
        {stats ? (
          <>
            <Stat
              label="Sessions"
              value={stats.totals.sessions}
              hint={`${stats.totals.active} running now`}
            />
            <Stat
              label="Completion rate"
              value={formatPercent(stats.totals.completionRate)}
              hint={`${stats.totals.completed} completed, ${stats.totals.failed + stats.totals.abandoned} not`}
            />
            <Stat
              label="First-pass rate"
              value={formatPercent(stats.totals.firstPassRate)}
              hint={`${stats.totals.verifiedSteps} steps verified`}
              tone={rateTone(stats.totals.firstPassRate)}
            />
            <Stat
              label="Average duration"
              value={formatDuration(stats.totals.avgDurationSeconds)}
              hint="completed sessions"
            />
            <Stat label="Retakes" value={stats.totals.retries} hint="photos asked again" />
            <Stat label="Technicians" value={stats.totals.technicians} hint="ran a session" />
          </>
        ) : (
          Array.from({ length: 6 }, (_, i) => (
            <div
              key={i}
              className="min-w-0 space-y-2 border-b border-line px-4 py-4 sm:px-5 xl:border-b-0 xl:[&:not(:first-child)]:border-l"
            >
              <Skeleton className="h-3.5 w-1/2" />
              <Skeleton className="h-7 w-2/5" />
              <Skeleton className="h-3 w-3/5" />
            </div>
          ))
        )}
      </StatBand>

      <div className="grid gap-5 xl:grid-cols-3">
        <Panel
          className={`xl:col-span-2 ${ROW_H}`}
          bodyClassName="flex min-h-0 flex-col"
          title="Sessions per day"
          action={
            <ChartLegend
              items={[
                { label: 'Started', color: 'var(--chart-1)' },
                { label: 'Completed', color: 'var(--chart-2)' },
                { label: 'Failed', color: 'var(--chart-3)' },
              ]}
            />
          }
        >
          {stats ? (
            stats.totals.sessions === 0 ? (
              <EmptyState
                title="No sessions in this window"
                body="Runs will chart here as they happen."
              />
            ) : (
              <div className="min-h-0 flex-1">
                <SessionsPerDay rows={stats.perDay} days={days} height={296} />
              </div>
            )
          ) : (
            <Skeleton className="h-full w-full" />
          )}
        </Panel>

        <Panel
          className={ROW_H}
          flush
          bodyClassName="min-h-0 overflow-y-auto"
          title="Latest verdicts"
          description="Newest first"
        >
          {stats ? (
            stats.recentVerdicts.length === 0 ? (
              <EmptyState title="No verdicts yet" />
            ) : (
              <ul className="divide-y divide-line">
                {stats.recentVerdicts.map((v) => (
                  <li key={v.auditId}>
                    <a
                      className="flex gap-3 px-4 py-3 transition-colors hover:bg-muted sm:px-5"
                      href={href({ name: 'session', id: v.sessionId })}
                    >
                      <Dot
                        className="mt-1.5"
                        tone={v.verified === true ? 'verified' : v.verified === false ? 'retry' : 'neutral'}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="truncate text-support font-semibold text-ink">
                            {v.technicianName ?? 'Technician'}
                            {v.stepNumber ? (
                              <span className="font-normal text-ink-dim"> · step {v.stepNumber}</span>
                            ) : null}
                          </span>
                          <span className="shrink-0 text-meta text-ink-faint">
                            {timeAgo(v.timestamp)}
                          </span>
                        </div>
                        <p className="mt-0.5 line-clamp-2 text-meta text-ink-dim">
                          {v.message ?? (v.verified ? 'Verified' : 'Retake requested')}
                        </p>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <SkeletonRows rows={6} />
          )}
        </Panel>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <Panel
          className={ROW_H}
          bodyClassName="flex min-h-0 flex-col"
          title="First-pass rate by step"
          description="Low bars are where people retake photos"
        >
          {stats ? (
            stats.perStep.length === 0 ? (
              <EmptyState title="No step data yet" />
            ) : (
              <div className="min-h-0 flex-1">
                <StepFirstPass rows={stats.perStep} height={296} />
              </div>
            )
          ) : (
            <Skeleton className="h-full w-full" />
          )}
        </Panel>

        <Panel className={ROW_H} flush bodyClassName="min-h-0 overflow-auto" title="Technicians">
          {stats ? (
            stats.technicians.length === 0 ? (
              <EmptyState title="No technicians ran a session" />
            ) : (
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-card">
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Sessions</TableHead>
                    <TableHead className="text-right">Completed</TableHead>
                    <TableHead className="text-right">First pass</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.technicians.map((t) => (
                    <TableRow key={t.userId}>
                      <TableCell className="font-medium text-ink">{t.name}</TableCell>
                      <TableCell className="text-right">{t.sessions}</TableCell>
                      <TableCell className="text-right">{t.completed}</TableCell>
                      <TableCell className="text-right">
                        <StatusBadge tone={toneFor(t.firstPassRate)}>
                          {formatPercent(t.firstPassRate)}
                        </StatusBadge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : (
            <SkeletonRows rows={5} />
          )}
        </Panel>
      </div>

      <Panel
        title="Equipment"
        action={
          <ChartLegend
            items={[
              { label: 'Sessions', color: 'var(--chart-1)' },
              { label: 'Completed', color: 'var(--chart-2)' },
            ]}
          />
        }
      >
        {stats ? (
          stats.equipment.length === 0 ? (
            <EmptyState title="No equipment was used" />
          ) : (
            <EquipmentBars rows={stats.equipment} />
          )
        ) : (
          <Skeleton className="h-40 w-full" />
        )}
      </Panel>
    </Page>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function rateTone(rate: number | null): 'up' | 'down' | undefined {
  if (rate === null) return undefined;
  if (rate >= 0.9) return 'up';
  if (rate < 0.7) return 'down';
  return undefined;
}

function toneFor(rate: number | null): 'neutral' | 'verified' | 'retry' | 'error' {
  if (rate === null) return 'neutral';
  if (rate >= 0.9) return 'verified';
  if (rate >= 0.7) return 'retry';
  return 'error';
}
